/* eslint-disable ember/no-controller-access-in-routes, no-unused-vars, warp-drive/no-legacy-request-patterns */
import { service } from '@ember/service';
import Route from '@ember/routing/route';
import {
  loadTemplateWithSections,
  templateDisplayName,
} from 'linced-app/utils/reporting';
import ReportTemplateSectionType from '@linc-technologies/linced-common/enums/reports/report-template-section-type';
import { isMobile } from '@linc-technologies/linced-common/utils/platform';

export default class WriteRoute extends Route {
  queryParams = {
    mode: { refreshModel: true },
    // Which reporting period is being written. Historic cards can route here
    // for a past period, so it cannot be assumed to be the current one.
    // Omitted (e.g. an old bookmark) falls back to the current period.
    periodIndex: { refreshModel: true },
    // Addresses the period across template versions — see the group
    // controller's qpPeriodEnd. Preferred over periodIndex when present.
    periodEndDate: { refreshModel: true },
    // The academic year the period belongs to — see the group controller's
    // qpPeriodYear. Absent means the current year.
    periodYear: { refreshModel: true },
  };

  // Services
  @service
  dates;

  @service
  store;

  // Resolves the period when the query param is absent — an old bookmark, or a
  // direct URL. The backend owns which period a template is currently in, so
  // this asks the counts response rather than recomputing it here.
  async _currentPeriodIndex(template) {
    let groupCtrl = this.controllerFor('reporting.group');
    try {
      await groupCtrl.loadGroupReports();
    } catch {
      // Counts unavailable — fall through to the first period rather than
      // failing the transition.
    }
    let entry = (groupCtrl.templateDraftInfo || {})[template.id];
    return entry && typeof entry.periodIndex === 'number'
      ? entry.periodIndex
      : 0;
  }

  async model(params) {
    let template = await loadTemplateWithSections(
      this.store,
      params.template_id,
    );

    // Query params arrive as strings; an absent one means "current period".
    let periodIndex =
      params.periodIndex === undefined ||
      params.periodIndex === null ||
      params.periodIndex === ''
        ? await this._currentPeriodIndex(template)
        : Number(params.periodIndex);

    let periodEndDate = params.periodEndDate || null;
    // Null means "current year" — the write controller resolves it, the same
    // way an absent periodIndex falls back to the current period.
    let periodYear =
      params.periodYear === undefined ||
      params.periodYear === null ||
      params.periodYear === ''
        ? null
        : Number(params.periodYear);
    let isLeadership = params.mode === 'leadership';
    let sections = this._getSections(template, isLeadership);

    if (!isLeadership) {
      // Preload subjects referenced by content sections
      let subjectIds = new Set();
      sections.forEach((section) => {
        let config = section.get('configuration');
        if (!config) return;
        try {
          let parsed = typeof config === 'string' ? JSON.parse(config) : config;
          if (parsed.subjectId) {
            subjectIds.add(parsed.subjectId);
          }
        } catch (err) {
          // ignore malformed config
        }
      });
      await Promise.all(
        Array.from(subjectIds).map((id) =>
          this.store.findRecord('goals/subject', id).catch(() => null),
        ),
      );

      // Preload expectations and levels so resolveLevelId works via peekAll
      await Promise.all([
        this.store.findAll('milestones/expectation').catch(() => null),
        this.store.findAll('milestones/level').catch(() => null),
      ]);
    }

    return {
      template,
      sections,
      isLeadership,
      periodIndex,
      periodEndDate,
      periodYear,
    };
  }

  setupController(controller, model) {
    super.setupController(...arguments);
    let groupCtrl = this.controllerFor('reporting.group');
    groupCtrl.set('isWriteRoute', true);
    // Name the template for the period being written. Rebuilt here because the
    // record is shared — whichever screen touched it last would otherwise win —
    // and because nothing else sets a period-bearing name any more.
    let entry = (groupCtrl.templateDraftInfo || {})[model.template.get('id')];
    model.template.set(
      'displayName',
      templateDisplayName(
        model.template,
        model.periodIndex,
        // The period's own year — a historic card can be writing a past one,
        // and the name would otherwise carry the current year.
        typeof model.periodYear === 'number'
          ? model.periodYear
          : this.dates.currentAcademicYear,
        entry,
      ),
    );
    groupCtrl.set('activeReport', model.template);
    let group = this.modelFor('reporting.group');
    controller.initWriteView(
      model.template,
      model.sections,
      group,
      model.isLeadership,
      model.periodIndex,
      model.periodEndDate,
      model.periodYear,
    );
  }

  // `renderTemplate`/`render` were removed in Ember 4.0. `templateName` is
  // the supported way to choose a template; a getter keeps it dynamic.
  get templateName() {
    return isMobile()
      ? 'reporting/group/write-mobile'
      : 'reporting/group/write';
  }

  resetController(controller, isExiting) {
    if (isExiting) {
      let groupCtrl = this.controllerFor('reporting.group');
      groupCtrl.set('isWriteRoute', false);
      controller.cleanupWriteView();
      // Period params are sticky per template. Cleared so the next visit that
      // doesn't name a period gets the current one, not whichever period was
      // last opened here (often a historic card's).
      controller.periodIndex = null;
      controller.periodEndDate = null;
      controller.periodYear = null;
    }
  }

  _getSections(template, isLeadership) {
    let templateId = template.id;
    let type = isLeadership
      ? ReportTemplateSectionType.PRINCIPAL_COMMENT
      : ReportTemplateSectionType.CONTENT;
    return this.store
      .peekAll('reports/report-template-section')
      .filter(
        // `reportTemplateId` is an async belongsTo, so reading `.id` straight
        // off it means reading through a promise proxy. Ask the relationship
        // reference for the id instead - it is synchronous and does not need
        // the related record to be loaded.
        (section) =>
          section.belongsTo('reportTemplateId').id() === templateId &&
          section.sectionType === type,
      )
      .sort((a, b) => a.sequence - b.sequence);
  }
}
